import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Circle, X, Rocket } from "lucide-react";
import { api } from "@/lib/api";

export default function OnboardingChecklist({ status, onHide }) {
  const { t } = useTranslation();
  if (!status || status.checklist_hidden) return null;
  if (status.trial?.status !== "trialing") return null;
  const { done, total } = status.progress || { done: 0, total: 0 };
  return (
    <section className="rounded-2xl border border-[#1473E6]/25 bg-white p-5" data-testid="onboarding-checklist">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Rocket className="h-4 w-4 text-[#1473E6]" />
          <h2 className="font-heading text-base font-extrabold text-slate-900">{t("ob_checklist_title")}</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-[#1473E6]" data-testid="onboarding-progress">
            {t("ob_progress", { done, total })}
          </span>
          <button onClick={onHide} data-testid="onboarding-hide" aria-label={t("ob_hide")}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-slate-400 hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100" data-testid="onboarding-bar">
        <div className="h-full rounded-full bg-[#1473E6] transition-all" style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
      </div>
      <ul className="mt-3 space-y-1">
        {status.checklist.map((s) => (
          <li key={s.key}>
            <Link to={s.href} data-testid={`onboarding-step-${s.key}`}
              className="flex min-h-[44px] items-center gap-3 rounded-xl px-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              {s.done
                ? <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
                : <Circle className="h-5 w-5 shrink-0 text-slate-300" />}
              <span className={s.done ? "text-slate-400 line-through" : ""}>{t(`ob_step_${s.key}`)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function TrialReminder({ status, onDismiss }) {
  const { t } = useTranslation();
  const r = status?.reminder;
  if (!r) return null;
  return (
    <div data-testid="trial-reminder" role="status"
      className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
      <div className="min-w-0 flex-1 text-sm font-semibold text-amber-900" data-testid="trial-reminder-text">
        {t(`ob_reminder_${r.key}`, { ...r.params, count: r.params?.days ?? 0 })}
      </div>
      <Link to={r.href} data-testid="trial-reminder-cta"
        className="inline-flex min-h-[44px] shrink-0 items-center rounded-full bg-[#1473E6] px-4 text-xs font-bold text-white active:scale-95">
        {t("ob_reminder_go")}
      </Link>
      <button onClick={() => onDismiss(r.key)} data-testid="trial-reminder-dismiss" aria-label={t("ob_hide")}
        className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-full text-amber-700 hover:bg-amber-100">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
